import React from 'react';
import { Link } from 'react-router-dom';
import { Recommendation } from '../../services/recommendations';

interface Props {
  recommendation: Recommendation;
}

export const RecommendationCard: React.FC<Props> = ({ recommendation }) => {
  
  const getReviewLevelColor = (level: string) => {
    switch (level) {
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (!recommendation) {
    return <div className="p-4 text-red-600">Error: No recommendation data</div>;
  }

  return (
    <div 
      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
      style={{ 
        border: '2px solid #000', 
        margin: '10px', 
        padding: '15px', 
        backgroundColor: '#f9f9f9',
        minHeight: '150px'
      }}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold" style={{ color: '#000', fontSize: '18px', fontWeight: 'bold' }}>
              {recommendation.traveler_name || 'Unknown Traveler'}
            </h3>
            <span 
              className={`px-2 py-1 rounded-full text-xs font-medium ${getReviewLevelColor(recommendation.overall_review_level)}`}
              style={{ padding: '4px 8px', backgroundColor: '#ffeb3b', color: '#000' }}
            >
              {recommendation.overall_review_level} RISK
            </span>
            {recommendation.validation_passed ? (
              <span className="text-green-600 text-sm" style={{ color: 'green' }}>✓ Validated</span>
            ) : (
              <span className="text-red-600 text-sm" style={{ color: 'red' }}>⚠ Not Validated</span>
            )}
          </div>

          <div className="text-sm text-gray-600 space-y-1" style={{ color: '#333', fontSize: '14px' }}>
            <div>Consultation: {recommendation.consultation_number}</div>
            <div>Created: {formatDate(recommendation.created_at)}</div>
            <div>
              Tokens: {recommendation.input_tokens?.toLocaleString() || 'N/A'} in / {recommendation.output_tokens?.toLocaleString() || 'N/A'} out
            </div>
            <div>Latency: {recommendation.latency_ms || 'N/A'}ms</div>
          </div>

          {/* Quick preview of recommendations */}
          <div className="mt-3 text-sm" style={{ marginTop: '12px', fontSize: '14px' }}>
            <div className="text-gray-700" style={{ color: '#000' }}>
              <strong>Vaccines:</strong> {recommendation.final_recommendations?.vaccines?.length || 0} recommended
            </div>
            <div className="text-gray-700" style={{ color: '#000' }}>
              <strong>Medications:</strong> {recommendation.final_recommendations?.medications?.length || 0} prescribed
            </div>
          </div>
        </div>

        <div className="text-right">
          <Link
            to={`/recommendations/${recommendation.id}`}
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            style={{ 
              display: 'inline-block', 
              padding: '8px 16px', 
              backgroundColor: '#2563eb', 
              color: '#fff', 
              textDecoration: 'none',
              borderRadius: '4px'
            }}
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};